import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  PROTOCOL_APPLIES,
  PROTOCOL_CATEGORY,
  PROTOCOL_STATUS,
  metaFor,
  type SafetyProtocol,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<SafetyProtocol>[] = [
  {
    key: "title",
    header: "Protocol",
    primary: true,
    render: (row) => (
      <div className="min-w-0 max-w-md">
        <p className="text-sm font-semibold text-foreground-950">{row.title}</p>
        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.description || "—"}</p>
      </div>
    ),
  },
  { key: "category", header: "Category", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.category}</span> },
  { key: "applies", header: "Applies to", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.applies_to || "—"}</span> },
  { key: "steps", header: "Steps", render: (row) => <span className="text-sm text-foreground-700">{(row.checklist || []).length}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(PROTOCOL_STATUS, row.status)} /> },
  { key: "reviewed", header: "Last reviewed", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.last_reviewed)}</span> },
];

const config: ResourceModuleConfig<SafetyProtocol> = {
  table: "safety_protocols",
  orderBy: "id",
  eyebrow: "Safety & Compliance",
  title: "Safety",
  subtitle: "The safety standards every hosting farm follows — briefings, first aid, field and waterway rules, food hygiene and emergency plans.",
  searchPlaceholder: "Search protocols, categories…",
  searchKeys: ["title", "category", "description", "applies_to"],
  filters: [
    { key: "category", label: "Category", options: options(PROTOCOL_CATEGORY) },
    { key: "status", label: "Status", options: optionsFrom(PROTOCOL_STATUS) },
  ],
  statusMap: PROTOCOL_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Protocols", value: rows.length, icon: "ri-shield-star-line", tone: "primary" },
    { label: "Active", value: rows.filter((row) => row.status === "active").length, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "Checklist steps", value: rows.reduce((sum, row) => sum + (row.checklist || []).length, 0), icon: "ri-list-check-2", tone: "secondary" },
    { label: "Drafts", value: rows.filter((row) => row.status !== "active").length, icon: "ri-draft-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "title", label: "Title" },
    { key: "category", label: "Category", type: "select", options: options(PROTOCOL_CATEGORY) },
    { key: "applies_to", label: "Applies to", type: "select", options: options(PROTOCOL_APPLIES) },
    { key: "status", label: "Status", type: "select", options: optionsFrom(PROTOCOL_STATUS) },
    { key: "last_reviewed", label: "Last reviewed", type: "date" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "checklist", label: "Checklist steps", type: "tags", placeholder: "One step, then another…" },
  ],
  createFields: [
    { key: "title", label: "Title" },
    { key: "category", label: "Category", type: "select", options: options(PROTOCOL_CATEGORY) },
    { key: "applies_to", label: "Applies to", type: "select", options: options(PROTOCOL_APPLIES) },
    { key: "description", label: "Description", type: "textarea" },
    { key: "checklist", label: "Checklist steps", type: "tags", placeholder: "One step, then another…" },
  ],
  newDefaults: {
    title: "",
    category: "General",
    description: "",
    applies_to: "All farms",
    checklist: [],
    status: "active",
    last_reviewed: null,
  },
  emptyTitle: "No safety protocols yet",
  emptyMessage: "Add the standards and checklists every host must follow to keep visits safe.",
  rowTitle: (row) => row.title,
  rowExtra: (row) =>
    (row.checklist || []).length > 0 ? (
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
          Checklist
        </p>
        <ul className="space-y-1.5">
          {(row.checklist || []).map((step) => (
            <li key={step} className="flex items-start gap-2 text-sm text-foreground-800">
              <i className="ri-checkbox-circle-line text-primary-600 mt-0.5"></i>
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null,
};

export default function AdminSafety() {
  return <ResourceModule config={config} />;
}